import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  collection,
  getCountFromServer,
  orderBy,
  query,
  where,
  type QueryConstraint,
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { foldTr } from "../lib/searchKeys";
import { useCollection } from "../hooks/useCollection";
import { usePagedQuery } from "../hooks/usePagedQuery";
import { DataTable, type Column } from "../components/DataTable";
import { Pagination } from "../components/Pagination";
import { DateRangePicker, type RangeValue } from "../components/DateRangePicker";
import { ChannelBadge, channelOf } from "../components/ChannelBadge";
import { RequireRole } from "../components/RequireRole";
import { LoadingBlock } from "../components/Spinner";
import { EmptyState } from "../components/EmptyState";
import { tl, n, date } from "../lib/format";
/**
 * ★ Aralık çözümü SUNUCU DOSYASINDAN doğrudan alınır (`lib/searchKeys.ts` ile
 * aynı gerekçe): `dateRange.ts` saf TypeScript'tir, hiç import'u yoktur ve
 * tarayıcıda derlenir. Kopyalasaydık "Son 7 gün" bu ekranda ve raporda farklı
 * gün sayar, iki rakam tutmaz ve hangisinin doğru olduğu tartışılırdı.
 */
import { resolveRange } from "../../../functions/src/maintenance/dateRange";

/**
 * İptal / iade / başarısız / reddedilen siparişler — eski `cancelled_orders.php`.
 *
 * ── ★ DÜZELTİLEN: VERİNİN %92'Sİ GİZLİYDU ──────────────────────────────────
 * Ekran her statü için `limit(25)` ile kesiyor ve "25 kayıt (en fazla 25)"
 * yazıyordu. Sonraki sayfaya geçiş YOKTU. Canlı veride 510 iptal + 131 iade +
 * 2 başarısız = 643 kayıt var; kullanıcı bunların 52'sini görebiliyordu.
 * Artık `usePagedQuery` (imleç tabanlı) + `Pagination` kullanılır: sayfa başına
 * 25/50/100/250 ve SUNUCUDAN SAYILAN toplam her zaman yazılır. Sessiz kırpma yok.
 *
 * ── ★ DÜZELTİLEN: ÜÇ AYRI TABLO → TEK SÜZÜLEBİLİR LİSTE ────────────────────
 * Eski düzen her statü için ayrı tablo basıyordu; sayfalama eklenince üç ayrı
 * sayfalayıcı olurdu ve "iptalin 3. sayfası, iadenin 1. sayfası" gibi takip
 * edilemez bir durum çıkardı. Tek liste + statü süzgeci hem sayfalanabilir hem
 * de iptali ve iadeyi AYRI AYRI görmeye izin verir.
 *
 * KORUNAN: statüler koda gömülü değil, `statuses` sözlüğünden gelir; salt
 * okunur; tarih DESC; yetki `boss` + `office` (yetki matrisi §6).
 */

/** Bir statünün "iptal benzeri" olup olmadığını teknik adından çıkarır. */
const CANCEL_PATTERNS = ["cancel", "iptal", "refund", "iade", "fail", "basarisiz", "reject", "red"];

function isCancelLike(technicName: string): boolean {
  const t = technicName.toLocaleLowerCase("tr-TR");
  return CANCEL_PATTERNS.some((p) => t.includes(p));
}

/** "Tümü" seçeneğinin süzgeç değeri — statü kimliği olamayacak bir dizi. */
const ALL = "__hepsi__";

/**
 * Firestore `in` sorgusu en fazla 30 değer kabul eder. Sözlükte 30'dan fazla
 * iptal benzeri statü olursa "Tümü" SESSİZCE eksik liste gösterirdi; bu yüzden
 * sınır aşılınca kullanıcıya yazılır ve tek statü seçmesi istenir.
 */
const IN_LIMIT = 30;

interface StatusDoc {
  label?: string;
  obsolete?: boolean;
  color?: string;
  sortOrder?: number;
  ordering?: number;
}

interface OrderRow {
  id: string;
  orderName?: string;
  customerFullName?: string;
  /**
   * ★ `refunded` BİLEREK opsiyonel ve `undefined` ile `0` AYRI anlamda:
   * alan yoksa iade tutarı BİLİNMİYOR, alan varsa ve 0 ise iade YOK.
   * Ayrıntı için aşağıdaki `refundCell` ve İADE açıklama kutusu.
   */
  totals?: { total?: number; refunded?: number; currentTotal?: number };
  createdAt?: { toDate: () => Date } | null;
  cancelledAt?: { toDate: () => Date } | null;
  cancelReason?: string;
  financialStatus?: string;
  erpStatus?: string;
  erpStatusLabel?: string;
  lineItemCount?: number;
  totalQuantity?: number;
  channel?: string | null;
  shopifyOrderId?: string | null;
  trendyol?: unknown;
}

/** Shopify `cancel_reason` kodlarının Türkçesi. Bilinmeyen kod aynen basılır. */
const CANCEL_REASON_TR: Record<string, string> = {
  CUSTOMER: "Müşteri isteği",
  DECLINED: "Ödeme reddedildi",
  FRAUD: "Sahtecilik şüphesi",
  INVENTORY: "Stok yetersiz",
  STAFF: "Personel hatası",
  OTHER: "Diğer",
};

/** Shopify `financial_status` kodları — iade/iptal bağlamında anlam taşır. */
const FINANCIAL_TR: Record<string, string> = {
  PAID: "Ödendi",
  VOIDED: "Provizyon iptal",
  REFUNDED: "İade edildi",
  PARTIALLY_REFUNDED: "Kısmi iade",
  PENDING: "Ödeme bekliyor",
  AUTHORIZED: "Provizyon alındı",
};

function reasonText(o: OrderRow): string | null {
  const raw = o.cancelReason?.trim();
  if (!raw) return null;
  return CANCEL_REASON_TR[raw.toUpperCase()] ?? raw;
}

/**
 * İADE HÜCRESİ — bu ekranın asıl hata kaynağı burasıydı, o yüzden ayrı fonksiyon.
 *
 * `totals.refunded` alanı canlı veride 14.623 siparişin YALNIZCA 15'inde var
 * (Shopify'dan gelenler); 14.606 WooCommerce göç kaydında alan HİÇ YOK. Yani
 * "İade Edildi" statüsündeki 131 siparişin hiçbirinde iade TUTARI yok — çünkü
 * göç bu bilgiyi taşımamış, yazan kod (`shopify/handlers/refunds.ts`) ise
 * yalnızca Shopify iade webhook'unda çalışıyor.
 *
 * Bu yüzden hücre İKİ DURUMU AYIRIR ve tutar UYDURMAZ:
 *   · alan var  → tutar yazılır (0 ise "₺0,00" — ölçülmüş bilgi, iade yok demek)
 *   · alan yok  → "—" ve sebebi ipucu olarak verilir (bilinmiyor demek)
 * `totals.total` ile `currentTotal` farkını iade sanmak YANLIŞ olurdu: fark
 * iptal edilmiş (VOIDED) provizyonda da oluşur, para iadesi anlamına gelmez.
 */
function refundCell(o: OrderRow): ReactNode {
  const v = o.totals?.refunded;
  if (typeof v === "number" && Number.isFinite(v)) {
    return v > 0 ? (
      <span className="font-medium text-gray-900">{tl(v)}</span>
    ) : (
      <span className="text-gray-500">{tl(0)}</span>
    );
  }
  return (
    <span
      className="cursor-help text-gray-400"
      title="İade tutarı BİLİNMİYOR: bu kayıtta totals.refunded alanı hiç yok (eski sistemden göç eden siparişlerde iade tutarı taşınmamış). '—' iade yok demek değildir."
    >
      —
    </span>
  );
}

/** Seçili aralığın Firestore sınırları. `max`/eksik özel aralıkta süzgeç yok. */
function useResolvedRange(range: RangeValue): { start: Date; end: Date; label: string } | null {
  return useMemo(() => {
    if (range.preset === "max") return null;
    if (range.preset === "custom" && (!range.from || !range.to)) return null;
    try {
      const r = resolveRange({
        preset: range.preset,
        from: range.from ?? null,
        to: range.to ?? null,
      });
      return { start: r.start, end: r.end, label: r.label };
    } catch {
      // Geçersiz özel aralık: süzgeç uygulanmaz, liste kırpılmaz.
      return null;
    }
  }, [range.preset, range.from, range.to]);
}

/* ────────────────────────────────────────────────────────────────────────── */

export function CancelledOrders() {
  const navigate = useNavigate();

  const statusesQ = useCollection<StatusDoc>(
    () => query(collection(db, "statuses"), where("category", "==", "orders")),
    [],
  );

  const sections = useMemo(
    () =>
      statusesQ.docs
        .filter((s) => s.obsolete !== true && isCancelLike(s.id))
        .sort((a, b) => (a.sortOrder ?? a.ordering ?? 10000) - (b.sortOrder ?? b.ordering ?? 10000)),
    [statusesQ.docs],
  );

  const [statusFilter, setStatusFilter] = useState<string>(ALL);
  const [range, setRange] = useState<RangeValue>({ preset: "max" });
  const [filter, setFilter] = useState("");

  const resolved = useResolvedRange(range);
  const startMs = resolved ? resolved.start.getTime() : null;
  const endMs = resolved ? resolved.end.getTime() : null;

  const ids = useMemo(() => sections.map((s) => s.id), [sections]);
  const idsKey = ids.join(",");
  const tooManyStatuses = ids.length > IN_LIMIT;

  const labelOf = useMemo(() => {
    const m = new Map<string, string>();
    sections.forEach((s) => m.set(s.id, s.label ?? s.id));
    return m;
  }, [sections]);

  // Sözlükten silinen/yeniden adlandırılan statü seçiliyse "Tümü"ne dönülür;
  // aksi hâlde liste sessizce boş kalırdı.
  useEffect(() => {
    if (statusFilter !== ALL && ids.length > 0 && !ids.includes(statusFilter)) {
      setStatusFilter(ALL);
    }
  }, [idsKey, statusFilter, ids]);

  /** Tarih süzgecinin Firestore kısıtları — sayım ve liste AYNI kısıtları kullanır. */
  const dateConstraints = useMemo<QueryConstraint[]>(() => {
    if (startMs === null || endMs === null) return [];
    // `end` HARİÇ (yarı açık aralık) — gün sınırında çift sayım olmaz.
    return [where("createdAt", ">=", new Date(startMs)), where("createdAt", "<", new Date(endMs))];
  }, [startMs, endMs]);

  const ordersQ = usePagedQuery<Omit<OrderRow, "id">>(
    () => {
      if (ids.length === 0) return null;
      const statusConstraint =
        statusFilter === ALL
          ? where("erpStatus", "in", ids.slice(0, IN_LIMIT))
          : where("erpStatus", "==", statusFilter);
      return query(
        collection(db, "orders"),
        statusConstraint,
        ...dateConstraints,
        orderBy("createdAt", "desc"),
      );
    },
    [idsKey, statusFilter, startMs, endMs],
    // Eski ekranın 25'lik penceresi VARSAYILAN olarak korunur — ama artık
    // duvar değil, sayfa boyutu: kullanıcı 250'ye çıkarabilir ve ilerleyebilir.
    25,
  );

  /**
   * STATÜ BAŞINA ADET — süzgeç düğmelerinin üstünde yazar.
   * Dokümanları indirmeden `count()` ile ölçülür ve tarih süzgecine UYAR;
   * aksi hâlde düğmede 510 yazarken listede 36 kayıt çıkar, sayı yalan olurdu.
   */
  const [counts, setCounts] = useState<Record<string, number | null>>({});
  const [countError, setCountError] = useState<string | null>(null);

  useEffect(() => {
    if (!idsKey) return;
    let cancelled = false;
    setCounts({});
    setCountError(null);

    Promise.all(
      idsKey.split(",").map(async (tech) => {
        const snap = await getCountFromServer(
          query(collection(db, "orders"), where("erpStatus", "==", tech), ...dateConstraints),
        );
        return [tech, snap.data().count] as const;
      }),
    )
      .then((pairs) => {
        if (!cancelled) setCounts(Object.fromEntries(pairs));
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        console.error("İptal/iade statü sayımı başarısız", e);
        // Sayım düşse bile liste çalışır; adet UYDURULMAZ, "—" yazılır.
        setCountError("Statü adetleri okunamadı. Liste yine de çalışıyor.");
      });

    return () => {
      cancelled = true;
    };
  }, [idsKey, dateConstraints]);

  const totalAll = useMemo(() => {
    const vals = ids.map((id) => counts[id]);
    if (vals.length === 0 || vals.some((v) => typeof v !== "number")) return null;
    return vals.reduce<number>((s, v) => s + (v as number), 0);
  }, [counts, ids]);

  const pageRows: OrderRow[] = ordersQ.docs;

  /**
   * SAYFA İÇİ SÜZME — yalnızca yüklenmiş satırlarda çalışır (Orders.tsx ile aynı
   * desen). Kapsam kullanıcıya AÇIKÇA yazılır; "süzdüm, çıkmadı, demek ki yok"
   * yanılgısının kaynağı bu bilginin gizlenmesiydi. `foldTr` iki tarafı da aynı
   * katlamadan geçirir: "atas" yazınca "Ataş" kaybolmaz.
   */
  const rows = useMemo(() => {
    const f = foldTr(filter.trim());
    if (!f) return pageRows;
    return pageRows.filter((o) =>
      foldTr(
        [o.orderName ?? "", o.customerFullName ?? "", o.id, reasonText(o) ?? ""].join(" "),
      ).includes(f),
    );
  }, [pageRows, filter]);

  /** Bu sayfada iade tutarı ÖLÇÜLMÜŞ kaç satır var — açıklama kutusu için. */
  const refundKnown = useMemo(
    () => rows.filter((o) => typeof o.totals?.refunded === "number").length,
    [rows],
  );

  const showStatusColumn = statusFilter === ALL;

  const columns: ReadonlyArray<Column<OrderRow>> = useMemo(() => {
    const cols: Array<Column<OrderRow>> = [
      {
        key: "order",
        header: "Sipariş",
        render: (o) => (
          <div className="flex flex-col gap-1">
            <Link
              to={`/orders/${o.id}`}
              className="link font-medium"
              onClick={(e) => e.stopPropagation()}
            >
              {o.orderName ?? o.id}
            </Link>
            {/* Kanal rozeti: İADE sütununun neden boş olduğunu satır satır
                açıklar — "Eski sistem" satırlarında iade tutarı hiç yazılmamış. */}
            <ChannelBadge channel={channelOf(o)} className="w-fit" />
          </div>
        ),
      },
    ];

    if (showStatusColumn) {
      cols.push({
        key: "status",
        header: "Statü",
        render: (o) => (
          <span className="whitespace-nowrap rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-200">
            {o.erpStatusLabel ?? labelOf.get(o.erpStatus ?? "") ?? o.erpStatus ?? "—"}
          </span>
        ),
      });
    }

    cols.push(
      {
        key: "customer",
        header: "Müşteri",
        render: (o) => o.customerFullName?.trim() || "—",
      },
      {
        key: "items",
        header: "Kalem / adet",
        hideOnMobile: true,
        render: (o) => `${n(o.lineItemCount)} / ${n(o.totalQuantity)}`,
      },
      {
        key: "total",
        header: "Toplam",
        numeric: true,
        // Sipariş tutarı üç role de açıktır; bu ekran zaten yalnızca ofis+patron.
        render: (o) => tl(o.totals?.total),
      },
      {
        key: "refunded",
        header: "İade",
        numeric: true,
        render: refundCell,
      },
      {
        key: "reason",
        header: "Sebep",
        hideOnMobile: true,
        render: (o) => {
          const r = reasonText(o);
          const fin = o.financialStatus
            ? (FINANCIAL_TR[o.financialStatus.toUpperCase()] ?? o.financialStatus)
            : null;
          if (!r && !fin) return <span className="text-gray-400">—</span>;
          return (
            <div className="flex flex-col gap-0.5">
              {r ? <span className="text-gray-800">{r}</span> : null}
              {fin ? <span className="text-xs text-gray-500">{fin}</span> : null}
            </div>
          );
        },
      },
      {
        key: "date",
        header: "Tarih",
        hideOnMobile: true,
        // İptal tarihi varsa o gösterilir; yoksa siparişin açılış tarihi.
        render: (o) => date(o.cancelledAt ?? o.createdAt),
      },
    );

    return cols;
  }, [showStatusColumn, labelOf]);

  const filterBusy = ordersQ.loading;

  return (
    <RequireRole roles={["boss", "office"]}>
      <div className="space-y-5">
        {/* ── Başlık ─────────────────────────────────────────────────────── */}
        <div>
          <nav aria-label="Sayfa yolu" className="text-sm">
            <ol role="list" className="flex items-center gap-2 text-gray-500">
              <li>
                <Link to="/orders" className="hover:text-gray-700">
                  Siparişler
                </Link>
              </li>
              <li aria-hidden="true">/</li>
              <li className="text-gray-700">İptal / İade</li>
            </ol>
          </nav>
          <h1 className="mt-2 text-2xl font-bold tracking-tight text-gray-950 sm:text-[28px] sm:leading-9">
            İptal / İade Siparişler
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Salt okunur liste. Statü değişikliği sipariş detayından yapılır.
          </p>
        </div>

        {statusesQ.error ? (
          <div
            role="alert"
            className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900"
          >
            {statusesQ.error}
          </div>
        ) : statusesQ.loading ? (
          <LoadingBlock />
        ) : sections.length === 0 ? (
          <EmptyState
            title="İptal/iade statüsü tanımlı değil"
            description="statuses koleksiyonunda iptal, iade, başarısız veya reddedildi anlamına gelen bir sipariş statüsü bulunamadı."
            tone="warning"
          />
        ) : (
          <>
            {/* ── Süzgeçler ────────────────────────────────────────────────
                Dar ekranda alt alta, tablette yan yana. Düğmeler 44px. */}
            <div className="space-y-3 rounded-lg bg-white p-3 shadow-sm ring-1 ring-gray-200 sm:p-4">
              {/* Statü süzgeci — iptal ve iade AYRI AYRI görülebilsin diye. */}
              <div>
                <p id="statu-suzgeci" className="label mb-1.5">
                  Statü
                </p>
                <div
                  role="group"
                  aria-labelledby="statu-suzgeci"
                  className="flex flex-wrap gap-2"
                >
                  <button
                    type="button"
                    aria-pressed={statusFilter === ALL}
                    disabled={tooManyStatuses}
                    onClick={() => setStatusFilter(ALL)}
                    className={
                      statusFilter === ALL
                        ? "btn-primary"
                        : "btn-secondary"
                    }
                  >
                    Tümü
                    <span className="ml-1 tabular-nums opacity-80">
                      ({totalAll === null ? "—" : n(totalAll)})
                    </span>
                  </button>

                  {sections.map((s) => {
                    const c = counts[s.id];
                    const active = statusFilter === s.id;
                    return (
                      <button
                        key={s.id}
                        type="button"
                        aria-pressed={active}
                        onClick={() => setStatusFilter(s.id)}
                        className={active ? "btn-primary" : "btn-secondary"}
                      >
                        {s.label ?? s.id}
                        <span className="ml-1 tabular-nums opacity-80">
                          ({c === undefined ? "…" : c === null ? "—" : n(c)})
                        </span>
                      </button>
                    );
                  })}
                </div>

                {tooManyStatuses ? (
                  <p role="alert" className="mt-1.5 text-xs text-amber-800">
                    {n(ids.length)} iptal/iade statüsü var; Firestore tek sorguda en fazla{" "}
                    {IN_LIMIT} statü birleştirebilir. "Tümü" eksik liste göstermemek için
                    kapatıldı — tek statü seçin.
                  </p>
                ) : null}

                {countError ? (
                  <p role="alert" className="mt-1.5 text-xs text-amber-800">
                    {countError}
                  </p>
                ) : null}
              </div>

              {/* Tarih aralığı + arama */}
              <div className="flex flex-col gap-3 border-t border-gray-100 pt-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <p className="label mb-1.5">Tarih aralığı</p>
                  <DateRangePicker
                    value={range}
                    onChange={setRange}
                    resolvedLabel={resolved?.label ?? null}
                    busy={filterBusy}
                  />
                </div>

                <div className="w-full sm:max-w-xs">
                  <label htmlFor="iptal-arama" className="label mb-1.5">
                    Bu sayfada ara
                  </label>
                  <input
                    id="iptal-arama"
                    type="search"
                    className="input min-h-[44px]"
                    placeholder="Sipariş no, müşteri, sebep"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                  />
                  {filter.trim() ? (
                    <p className="mt-1 text-xs text-gray-600">
                      Yalnızca yüklenmiş {n(pageRows.length)} satır süzüldü, {n(rows.length)}{" "}
                      sonuç. Tüm siparişlerde aramak için{" "}
                      <Link
                        to={`/search?q=${encodeURIComponent(filter.trim())}`}
                        className="font-semibold text-primary-700 underline"
                      >
                        genel aramayı
                      </Link>{" "}
                      kullanın.
                    </p>
                  ) : null}
                </div>
              </div>

              {(range.preset !== "max" || filter.trim() || statusFilter !== ALL) ? (
                <div className="border-t border-gray-100 pt-3">
                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => {
                      setStatusFilter(ALL);
                      setRange({ preset: "max" });
                      setFilter("");
                    }}
                  >
                    Süzgeçleri temizle
                  </button>
                </div>
              ) : null}
            </div>

            {/* ── ★ İADE SÜTUNU AÇIKLAMASI ─────────────────────────────────
                Sütunun neden çoğu satırda boş olduğu EKRANDA yazar. Boş bir
                sütunu sessizce göstermek, kullanıcıya "iade yok" dedirtiyordu. */}
            <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <p className="font-semibold">İADE sütunu neden çoğu satırda boş?</p>
              <p className="mt-1">
                İade tutarı yalnızca Shopify'ın iade bildirimi geldiğinde{" "}
                <code className="rounded bg-amber-100 px-1 py-0.5 text-xs">totals.refunded</code>{" "}
                alanına yazılır. Eski sistemden (WooCommerce) göç eden siparişlerde bu alan HİÇ
                YOK — göç iade tutarını taşımamış. Bu satırlarda{" "}
                <strong>“—” iade yok demek değil, tutar bilinmiyor demektir.</strong> Tutar
                uydurulmaz.
              </p>
              <p className="mt-1 text-xs">
                Bu sayfadaki {n(rows.length)} satırın {n(refundKnown)} tanesinde iade tutarı
                ölçülmüş durumda.
              </p>
            </div>

            {/* ── Liste ────────────────────────────────────────────────────
                Geniş tablo DataTable'ın kendi `overflow-x-auto` kabında kayar;
                sayfa gövdesi yatay kaymaz. */}
            <DataTable<OrderRow>
              caption="İptal ve iade siparişleri"
              rows={rows}
              columns={columns}
              rowKey={(o) => o.id}
              loading={ordersQ.loading}
              error={ordersQ.error}
              emptyTitle={
                filter.trim() ? "Aramaya uyan kayıt yok" : "Bu süzgeçte sipariş yok"
              }
              emptyDescription={
                filter.trim()
                  ? "Arama yalnızca bu sayfadaki satırlarda çalışır. Sonraki sayfaya geçin veya genel aramayı kullanın."
                  : "Tarih aralığını genişletmeyi veya başka bir statü seçmeyi deneyin."
              }
              onRowClick={(o) => navigate(`/orders/${o.id}`)}
            />

            {/* Sayfalama: sayfa boyutu + kaçıncı aralık + SUNUCUDAN toplam. */}
            {rows.length > 0 || ordersQ.page > 1 ? (
              <Pagination
                page={ordersQ.page}
                pageSize={ordersQ.pageSize}
                total={ordersQ.total}
                shown={pageRows.length}
                hasNext={ordersQ.hasNext}
                hasPrev={ordersQ.hasPrev}
                onNext={ordersQ.next}
                onPrev={ordersQ.prev}
                onPageSize={ordersQ.setPageSize}
                loading={ordersQ.loading}
              />
            ) : null}
          </>
        )}
      </div>
    </RequireRole>
  );
}

export default CancelledOrders;
